import { defineMessage, useIntl } from 'react-intl-next';

import { SPACE_SETTINGS_EXPORT_SPACE_UNAVAILABLE } from '@confluence/named-routes/entry-points/routes.ts';
import { ExportPolicyEnum } from '@confluence/export-space/entry-points/ExportPolicyEnum.ts';
import { useExportEnabledState } from '@confluence/export-space/entry-points/useExportEnabledState.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import type {
	SpaceSettingsWebItemSectionsFragment_webItemSections,
	SpaceSettingsWebItemSectionsFragment_webItemSections_items,
} from './__types__/SpaceSettingsWebItemSectionsFragment.ts';
import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector.ts';

export const SPACE_OVERVIEW_WEBITEM_SECTION_ID = '-system.space.tools-overview';
export const SPACE_EXPORT_SPACE_WEBITEM_ID = 'space-export-space';

const exportSpaceMessage = defineMessage({
	id: 'space-settings.web-item-section.space-permissions.web-item.export-space',
	defaultMessage: 'Export space',
	description: 'Label for the web item link for export space',
});

const addExportSpaceWebItem = (
	exportSpaceWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items,
	webItemSections:
		| ReadonlyArray<SpaceSettingsWebItemSectionsFragment_webItemSections | null>
		| undefined,
) => {
	if (!exportSpaceWebItem) {
		return webItemSections;
	}

	return webItemSections?.map((section) => {
		if (section?.id === SPACE_OVERVIEW_WEBITEM_SECTION_ID) {
			// XXX Don't mutate the input webItemSections! May be shared across invocations.
			const items = section.items.map((item) =>
				item?.completeKey === 'confluence.sections.space.tools.v2:export'
					? exportSpaceWebItem
					: item,
			);
			return {
				...section,
				items,
			};
		}
		return section;
	});
};

export const useExportSpaceWebItem: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
}) => {
	const intl = useIntl();
	const spaceKey = useSpaceKey();
	const spaceId = space?.id;

	const { exportPolicyState } = useExportEnabledState(spaceId);

	if (
		!spaceKey ||
		!spaceId ||
		exportPolicyState === ExportPolicyEnum.LOADING ||
		exportPolicyState === ExportPolicyEnum.ENABLED
	) {
		return webItemSections;
	}

	const exportSpaceWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items = {
		id: SPACE_EXPORT_SPACE_WEBITEM_ID,
		label: intl.formatMessage(exportSpaceMessage),
		url: SPACE_SETTINGS_EXPORT_SPACE_UNAVAILABLE.toUrl({ spaceKey }),
		completeKey: null,
		icon: null,
	};

	return addExportSpaceWebItem(exportSpaceWebItem, webItemSections);
};
